import React from "react";
import PolicyLayout from "../components/PolicyLayout";
import { useSeo } from "../lib/seo";

/**
 * Terms of Service. Same boilerplate caveat as Privacy: bracketed
 * placeholders MUST be replaced by counsel before final go-live. Drafted
 * to cover what a US-targeted prepaid/virtual card platform typically
 * needs: eligibility, account use, fees, prohibited activities, dispute
 * resolution / arbitration, governing law.
 */
export default function TermsOfService() {
  useSeo({
    title: "Terms of Service",
    description: "The terms and conditions governing your use of the Zcardon virtual debit card platform.",
    path: "/terms",
  });
  return (
    <PolicyLayout
      eyebrow="Company Policy"
      title="Terms of Service"
      effective="Feb 1, 2026"
      lastUpdated="Feb 6, 2026"
      testId="terms-of-service-page"
    >
      <p>
        These Terms of Service (the &quot;<strong>Terms</strong>&quot;) form a
        binding agreement between you and <strong>Zeborg Water Solution Corp.</strong>{" "}
        (&quot;<strong>Zcardon</strong>&quot;) and govern your use of the
        Zcardon virtual debit card platform at <a href="https://zcardon.com">zcardon.com</a>{" "}
        (the &quot;<strong>Service</strong>&quot;). By creating an account or
        using the Service, you accept these Terms in full. If you do not
        accept these Terms, do not use the Service.
      </p>

      <h2>1. Eligibility</h2>
      <ul>
        <li>You must be at least <strong>18 years old</strong> and a resident of the United States.</li>
        <li>You must provide accurate identity information and complete any KYC checks we or our partners require.</li>
        <li>You must not be on any US sanctions list (OFAC SDN) or located in a comprehensively sanctioned jurisdiction.</li>
      </ul>

      <h2>2. Your account</h2>
      <p>
        You are responsible for all activity that occurs under your account
        and for keeping your password and 2FA factor secure. Notify us
        immediately at <a href="mailto:security@zcardon.com">security@zcardon.com</a>{" "}
        if you suspect unauthorized access.
      </p>

      <h2>3. The Service</h2>
      <p>
        Zcardon lets you load funds via ACH or Stripe-supported payment
        methods and issue closed-loop virtual cards that a receiver can use
        for QR-based payments at participating merchants, or for cash
        withdrawal where supported. Zcardon is <strong>not a bank</strong>;
        funds are held with our partner financial institutions under their
        respective terms.
      </p>

      <h2>4. Fees and limits</h2>
      <p>
        Current fees and per-transaction / daily limits are published at <a href="/rates">zcardon.com/rates</a>{" "}
        and may change with at least 30 days&apos; notice. We may apply lower
        limits or holds where required by AML risk policy.
      </p>

      <h2>5. Prohibited activities</h2>
      <p>You may not, and you may not allow any third party to:</p>
      <ul>
        <li>Use the Service for illegal activity, including but not limited to fraud, money laundering, terrorist financing, or evasion of sanctions.</li>
        <li>Use the Service to purchase or facilitate purchases of restricted goods (firearms, controlled substances, adult content, gambling outside licensed jurisdictions, etc.).</li>
        <li>Issue cards to recipients you do not have the right to contact.</li>
        <li>Send unsolicited messages or use the Service to harass any person.</li>
        <li>Attempt to access another user&apos;s account, reverse-engineer the Service, or interfere with normal operation (rate-limit evasion, scraping, automated abuse).</li>
        <li>Mis-represent yourself or another party to us or our partners.</li>
      </ul>

      <h2>6. Cardholder Agreement</h2>
      <p>
        When you accept a card issued by a Zcardon partner issuing bank, a
        separate <a href="/issuing/accept-terms/sample">Cardholder Agreement</a> may apply.
        That agreement governs the card itself; these Terms govern your use
        of the Service.
      </p>

      <h2>7. Agent program</h2>
      <p>
        If you apply to become a Zcardon Agent, additional terms apply: you
        earn a 0.5% commission on completed customer activity you assist
        with; your account remains in &quot;pending&quot; status until an
        Admin approves you; we may suspend or terminate your agent status at
        our sole discretion. SSN and identity information you provide is
        encrypted at rest under our <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>8. SMS, email, and notifications</h2>
      <p>
        Use of the Service requires that we send you transactional
        communications described in our <a href="/sms-policy">SMS Policy</a>{" "}
        and <a href="/privacy">Privacy Policy</a>. You may opt out of
        marketing emails at any time; transactional SMS opt-out is described
        in the SMS Policy.
      </p>

      <h2>9. Termination</h2>
      <p>
        We may suspend or terminate your account at our discretion if you
        violate these Terms, present risk to other users or partners, or if
        we are required to do so by law. You may close your account at any
        time by emailing <a href="mailto:hello@zcardon.com">hello@zcardon.com</a>{" "}
        — we will return any remaining balance to your bank of record after
        completing required compliance checks.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;
        WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING
        WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        TO THE FULLEST EXTENT PERMITTED BY LAW, ZCARDON&apos;S AGGREGATE
        LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR
        THE SERVICE IS LIMITED TO THE GREATER OF (A) THE FEES YOU PAID
        ZCARDON IN THE 12 MONTHS PRECEDING THE CLAIM OR (B) $100.
      </p>

      <h2>12. Dispute resolution &amp; arbitration</h2>
      <p>
        You and Zcardon agree to first attempt to resolve any dispute
        informally by contacting <a href="mailto:legal@zcardon.com">legal@zcardon.com</a>.
        If we cannot resolve within 60 days, the dispute will be resolved by
        binding individual arbitration under the AAA Consumer Arbitration
        Rules, in Texas, USA. <strong>You waive any right to participate
        in a class action.</strong> The Federal Arbitration Act governs.
      </p>

      <h2>13. Governing law</h2>
      <p>
        These Terms are governed by the laws of the State of <strong>Texas</strong>, USA, without regard to
        conflict-of-laws principles.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be
        announced via email and on this page at least 30 days before taking
        effect. Continued use of the Service after the effective date
        constitutes acceptance.
      </p>

      <h2>15. Contact</h2>
      <p>
        <strong>Zeborg Water Solution Corp.</strong><br />
        2223 Arundel Crossing Dr, Sugar Land, TX 77479<br />
        Legal: <a href="mailto:legal@zcardon.com">legal@zcardon.com</a><br />
        Support: <a href="mailto:hello@zcardon.com">hello@zcardon.com</a> · <a href="/support">/support</a>
      </p>
    </PolicyLayout>
  );
}
